import { useNavigate, useParams } from "react-router-dom";
import MainContainer from "../../layout/MainContainer";
import { Box, Tab, Tabs } from "@mui/material";
import React, { useEffect, useState } from "react";
import TableComponent from "../../components/TableComponent";
import { ENUM_ROUTES } from "../../RoutesEnum";
import { showError } from "../../constants/toasts";
import { useLazyGetNewsDetailQuery } from "../../services/news";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}
export interface NewsApiResponse {
  _id: string;
  image: string;
  authorName: string;
  description: string;
  title: string;
  status: boolean;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

function CustomTabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <>{children}</>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `tab-${index}`,
    "aria-controls": `tabpanel-${index}`,
  };
}

const NewsDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [valueTabs, setValueTabs] = React.useState(0);
  const [newsDetails] = useLazyGetNewsDetailQuery();
  const [loading, setLoading] = useState(false);
  const [newsData, setNewsData] = useState<NewsApiResponse>();

  const getDataByIdList = async () => {
    try {
      setLoading(true);
      if (!id) return;
      const response = await newsDetails({ id }).unwrap();
      if (response.statusCode === 200) {
        setNewsData(response.data);
      }
      setLoading(false);
    } catch (error: any) {
      setLoading(false);
      showError(error?.message || error?.data?.message);
    }
  };

  useEffect(() => {
    if (id) {
      getDataByIdList();
    }
  }, [id]);
  const handleChangeTabs = (event: React.SyntheticEvent, newValue: number) => {
    setValueTabs(newValue);
  };

  // Format date function
  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString();
  };

  // Format status function
  const formatStatus = (status: boolean) => {
    return status ? "Active" : "Inactive";
  };

  // Dynamic venue rows based on news data (if available)
  const venueRows = newsData
    ? [
        {
          id: 1,
          name: newsData.title || "N/A",
          date: formatDate(newsData.createdAt),
          status: formatStatus(newsData.status),
          duration: "N/A", // Add if available in API
          frequency: "N/A", // Add if available in API
        },
      ]
    : [];

  const venueColumns = [
    { field: "name", headerName: "Venue", flex: 1, minWidth: 150 },
    { field: "date", headerName: "Start Date", flex: 1, minWidth: 130 },
    { field: "duration", headerName: "Duration", flex: 1, minWidth: 130 },
    { field: "frequency", headerName: "Frequency", flex: 1, minWidth: 130 },
    { field: "status", headerName: "Status", flex: 1, minWidth: 100 },
  ];

  if (loading) {
    return (
      <MainContainer>
        <div className="main_title">
          <h1>News Details</h1>
          <p>
            <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
            <span onClick={() => navigate(ENUM_ROUTES.MANAGE_NEWS)}>
              Manage News
            </span>{" "}
            - News Details
          </p>
        </div>
        <div className="cards view_page">
          <div className="section_title">
            <h2>Loading...</h2>
          </div>
        </div>
      </MainContainer>
    );
  }

  return (
    <MainContainer>
      <div className="main_title">
        <h1>News Details</h1>
        <p>
          <span onClick={() => navigate("/dashboard")}>Dashboard</span> -{" "}
          <span onClick={() => navigate(ENUM_ROUTES.MANAGE_NEWS)}>
            Manage News
          </span>{" "}
          - News Details
        </p>
      </div>

      <Box className="custom_tabs1" sx={{ width: "100%" }}>
        <Tabs
          value={valueTabs}
          onChange={handleChangeTabs}
          className="custom_tabs_links"
        >
          <Tab label="General Details" {...a11yProps(0)} />
        </Tabs>
      </Box>
      <div style={{ marginTop: 20 }}></div>
      <CustomTabPanel value={valueTabs} index={0}>
        <div className="cards view_page">
          <div className="section_title">
            <h2>News Information</h2>
          </div>
          <div className="gap_p">
            <div className="vew_img w_100">
              <h6>News Image</h6>
              <figure>
                <img
                  alt="news"
                  src={newsData?.image || "/static/images/placeholder.png"}
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.src = "/static/images/placeholder.png";
                  }}
                />
              </figure>
            </div>
            <div className="w_33">
              <h6>News Title</h6>
              <p>{newsData?.title || "N/A"}</p>
            </div>
            <div className="w_33">
              <h6>Author Name</h6>
              <p>{newsData?.authorName || "N/A"}</p>
            </div>
            <div className="w_33">
              <h6>Status</h6>
              <p>{formatStatus(newsData?.status || false)}</p>
            </div>
            <div className="w_33">
              <h6>Created Date</h6>
              <p>{formatDate(newsData?.createdAt || "")}</p>
            </div>
            <div className="w_33">
              <h6>Updated Date</h6>
              <p>{formatDate(newsData?.updatedAt || "")}</p>
            </div>
            <div className="w_100">
              <h6>Description</h6>
              <p>{newsData?.description || "No description available"}</p>
            </div>
          </div>
        </div>
      </CustomTabPanel>

      <CustomTabPanel value={valueTabs} index={1}>
        <div className="cards">
          <TableComponent columns={venueColumns} rows={venueRows} />
        </div>
      </CustomTabPanel>
    </MainContainer>
  );
};

export default NewsDetails;
